import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { pillarArticles } from "@/mocks/pillarArticles";

export default function SiteFooter() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const handleAnchorClick = (hash: string) => {
    if (location.pathname === "/") {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      navigate(`/#${hash}`);
    }
  };
  const categoryKeys = [
    "shoes",
    "hoodies",
    "tshirts",
    "jackets",
    "pants",
    "headwear",
    "sets",
    "underwear",
    "jersey",
    "accessories",
    "bags",
    "belts",
    "perfumes",
    "other",
  ];

  return (
    <footer className="bg-primary-950 text-background-50">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-50 text-primary-700">
                <i className="ri-stack-line text-lg"></i>
              </span>
              <span className="font-heading text-lg font-bold">{t("brand.name")}</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-background-100/80">{t("footer.disclaimer")}</p>
          </div>

          {/* Explore */}
          <div>
            <h4 className="font-heading text-sm font-semibold uppercase tracking-wide text-background-200">
              {t("footer.explore")}
            </h4>
            <ul className="mt-4 space-y-2">
              <li>
                <Link to="/" className="text-sm text-background-100/80 hover:text-background-50 transition-colors">
                  {t("nav.home")}
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => handleAnchorClick("categories")}
                  className="text-sm text-background-100/80 hover:text-background-50 transition-colors text-left cursor-pointer"
                >
                  {t("nav.categories")}
                </button>
              </li>
              <li>
                <Link to="/article" className="text-sm text-background-100/80 hover:text-background-50 transition-colors">
                  {t("nav.article")}
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => handleAnchorClick("faq")}
                  className="text-sm text-background-100/80 hover:text-background-50 transition-colors text-left cursor-pointer"
                >
                  {t("nav.faq")}
                </button>
              </li>
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-heading text-sm font-semibold uppercase tracking-wide text-background-200">
              {t("footer.categories")}
            </h4>
            <ul className="mt-4 grid grid-cols-2 gap-2">
              {categoryKeys.slice(0, 8).map((key) => {
                const cat = categories.find((c) => c.id === key);
                return (
                  <li key={key}>
                    <a
                      href={cat?.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-background-100/80 hover:text-background-50 transition-colors"
                    >
                      {t(`categories.${key}`)}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-heading text-sm font-semibold uppercase tracking-wide text-background-200">
              {t("footer.resources")}
            </h4>
            <ul className="mt-4 space-y-2">
              {pillarArticles.slice(0, 5).map((article, idx) => (
                <li key={article.slug}>
                  <Link
                    to={`/guide/${article.slug}`}
                    className="text-sm text-background-100/80 hover:text-background-50 transition-colors line-clamp-1"
                  >
                    {t(`pillar.${idx}.title`)}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-background-100/80 hover:text-background-50 transition-colors"
                >
                  {t("buttons.usfansSpreadsheet")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-background-100/20 pt-6">
          <p className="text-sm text-background-100/70">
            © {new Date().getFullYear()} {t("brand.name")}. {t("footer.rights")}
          </p>
          <Link to="/" className="text-sm text-background-100/70 hover:text-background-50 transition-colors">
            {t("footer.backHome")}
          </Link>
        </div>
      </div>
    </footer>
  );
}